// hooks/maintenance/useComponents.js

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    fetchComponents,
    fetchComponentById,
    fetchComponentsByVehicleId,
    createComponent,
    updateComponent,
    deleteComponent,
} from "../../api/maintenance/componentService";

export const useComponents = (vehId = null) => {
    const queryClient = useQueryClient();

    const queryKey = vehId ? ["components", "byVehicle", vehId] : ["components"];
    const queryFn = vehId ? () => fetchComponentsByVehicleId(vehId) : fetchComponents;

    const {
        data: components = [],
        error,
        isLoading,
        isFetching,
    } = useQuery({
        queryKey: queryKey,
        queryFn: queryFn,
        enabled: true,
        staleTime: 1000 * 60 * 1,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });

    const invalidateCache = () => {
        queryClient.invalidateQueries({ queryKey: ["components"] });
    };

    const createMutation = useMutation({
        mutationFn: createComponent,
        onSuccess: (newComponentData) => {
            queryClient.invalidateQueries({ queryKey: ["components"] });
            queryClient.invalidateQueries({ queryKey: ["components", "byVehicle"] });
            queryClient.setQueryData(["component", newComponentData.comid], newComponentData);
            queryClient.refetchQueries({ queryKey: ["component", newComponentData.comid], exact: true });
        },
    });

    const updateMutation = useMutation({
        mutationFn: updateComponent,
        onSuccess: (updatedComponentData) => {
            invalidateCache();
            queryClient.setQueryData(["component", updatedComponentData.comid], updatedComponentData);
            queryClient.refetchQueries({ queryKey: ["component", updatedComponentData.comid], exact: true });
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteComponent,
        onSuccess: invalidateCache,
    });

    return {
        components,
        isLoading,
        isFetching,
        error,
        createMutation,
        updateMutation,
        deleteMutation,
    };
};